import FavoritesStore from '../../../store/schema/FavoritesStore';
import favoritesStore from '../../../store/store';
import * as favoriteNodeParser from '../../helpers/favoriteNodeParser';
import * as favoritesBitFlagsActions from '../../helpers/favoritesBitFlagsActions';
import { loadFavoriteNodesToStore, tryAddSingleFavoriteFolderToStore } from '../loadFavoritesV1';
import { FolderForestNodeType, FolderForestNode } from 'owa-favorites-types';
import UserOptionsType from 'owa-service/lib/contract/UserOptionsType';
import userConfigurationDictionaryEntry from 'owa-service/lib/factory/userConfigurationDictionaryEntry';
import userConfigurationDictionaryObject from 'owa-service/lib/factory/userConfigurationDictionaryObject';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import updateUserConfiguration from 'owa-session-store/lib/services/updateUserConfiguration';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import { action, select } from 'satcheljs/lib/legacy';

export interface MigrateFavoritesState {
    favoritesStore: FavoritesStore;
}

/**
 * Update local and server userOptions.FavoriteNodes and userOptions.FavoritesBitFlags
 * @param favoritesStore the favorites store
 * @param userOptions the user options
 */
function updateFavoritesAndMarkMigrationComplete(
    favoritesStore: FavoritesStore,
    userOptions: UserOptionsType
) {
    const newFavoritesNodesRaw = favoriteNodeParser.serialize(
        favoritesStore.orderedFavoritesNodeIds,
        favoritesStore.favoritesFolderNodes,
        favoritesStore.favoritesPersonaNodes,
        favoritesStore.favoriteSearches,
        favoritesStore.favoriteCategories
    );

    // Update the local user configuration store
    userOptions.FavoriteNodes = newFavoritesNodesRaw;
    favoritesBitFlagsActions.setBit(
        true /* value*/,
        favoritesBitFlagsActions.FavoritesBitFlagsMasks.FirstRunFavoritesMigration
    );

    // Send out request to update both UserOptions.FavoriteNodes and FavoritesBitFlags
    updateUserConfiguration('OWA.UserOptions', [
        userConfigurationDictionaryEntry({
            DictionaryKey: userConfigurationDictionaryObject({
                Type: 'String',
                Value: ['FavoriteNodes'],
            }),
            DictionaryValue: userConfigurationDictionaryObject({
                Type: 'StringArray',
                Value: newFavoritesNodesRaw,
            }),
        }),
        userConfigurationDictionaryEntry({
            DictionaryKey: userConfigurationDictionaryObject({
                Type: 'String',
                Value: ['FavoritesBitFlags'],
            }),
            DictionaryValue: userConfigurationDictionaryObject({
                Type: 'Integer32',
                Value: [`${userOptions.FavoritesBitFlags}`],
            }),
        }),
    ]);
}

/**
 * Migrate single favorite folder
 * @param folderId the folder to be migrated
 */
function tryMigrateSingleFavoriteFolder(folderId: string) {
    const favoriteFolderNode = <FolderForestNode>{
        id: folderId,
        type: FolderForestNodeType.Folder,
    };

    tryAddSingleFavoriteFolderToStore(favoriteFolderNode);
}

/**
 * Migrate the favorites for the first run experience
 * @param state the MigrateFavoritesState
 */
export default action('migrateFavorites')(
    select({
        favoritesStore: () => favoritesStore,
    })(function migrateFavorites(state?: MigrateFavoritesState) {
        const { favoritesStore } = state;

        // 1. Automatically populate default folders it they don't exist in favorites
        const defaultFavoriteFolderIds = [
            folderNameToId('inbox'),
            folderNameToId('drafts'),
            folderNameToId('archive'),
        ];
        defaultFavoriteFolderIds.forEach(folderId => tryMigrateSingleFavoriteFolder(folderId));

        // 2. Load existing UserOptions.FavoriteNodes to store if there is any
        loadFavoriteNodesToStore(favoritesStore);

        // 3. Migrate de-duped UserOptions.FavoriteFolders(used by JsMVVM) to FavoriteNodes(used by React), if there is any
        const userOptions = getUserConfiguration().UserOptions;
        if (userOptions.FavoriteFolders) {
            userOptions.FavoriteFolders.forEach(folderId =>
                tryMigrateSingleFavoriteFolder(folderId)
            );
        }

        // 4. Submit the service call to update UserOptions on the client and server
        updateFavoritesAndMarkMigrationComplete(favoritesStore, userOptions);
    })
);
